import { Component, inject, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { environment } from '@env/environment';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { filter } from 'rxjs/operators';
import { NavMode, ShellService } from '@app/shell/services/shell.service';
import { NavMenuItem } from '@core/types';
import { webSidebarMenuItems } from '@core/constants';
import { DialogService } from '@ngneat/dialog';
import { CredentialsService } from '@auth';
import { AddElementComponent } from '@shared/components';

@UntilDestroy({ checkProperties: true })
@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
  standalone: false,
})
export class SidebarComponent implements OnInit {
  shellService = inject(ShellService);
  version: string = environment.version;
  year: number = new Date().getFullYear();
  sidebarItems: NavMenuItem[] = [];
  sidebarExtendedItem = -1;
  navExpanded = false;
  suffixMap = {
    production: '',
    staging: '-staging',
    development: '-dev',
    local: '-local',
  };
  private readonly _router = inject(Router);
  private readonly _dialogService = inject(DialogService);
  private readonly _credentialsService = inject(CredentialsService);

  constructor() {
    this.sidebarItems = webSidebarMenuItems;

    this.version = environment.version + (this.suffixMap[environment.environment] || '');
  }

  get name(): string | null {
    const credentials = this._credentialsService.credentials;
    return credentials ? credentials?.firstName + ' ' + credentials?.lastName : null;
  }

  get clubDetails(): any {
    return this._credentialsService?.credentials?.club;
  }

  get user(): any {
    return this._credentialsService?.credentials?.firstName;
  }

  ngOnInit(): void {
    this.shellService.activeNavTab(this.sidebarItems);

    this._router.events
      .pipe(untilDestroyed(this))
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe({
        next: () => {
          this.shellService.activeNavTab(this.sidebarItems);
        },
      });

    this.shellService.navMode$.pipe(untilDestroyed(this)).subscribe({
      next: (mode) => {
        this.navExpanded = mode === NavMode.Locked;
      },
    });

    // update sideBar subitems in of item href:'/club/add-ons'
    const clubAddOnsIndex = this.sidebarItems.findIndex((item) => item.href === '/club/add-ons');
    if (clubAddOnsIndex > -1) {
      this.sidebarItems[clubAddOnsIndex].subItems = [
        { url: this.clubDetails?.linkToVV || '', icon: 'user', title: 'Club Administration' },
        { url: this.clubDetails?.linkToFIBU || '', icon: 'receipt', title: 'Financial Accounting' },
        { url: 'https://admin.verein.cloud/', icon: 'user-id-outline', title: 'License & More' },
      ];
    }
  }

  toggleSidebar(isEnterEvent: boolean): void {
    this.shellService.navMode$.pipe(untilDestroyed(this)).subscribe({
      next: (mode) => {
        if (isEnterEvent) {
          this.navExpanded = true;
        } else if (!isEnterEvent && mode === NavMode.Free) {
          this.navExpanded = false;
        }
      },
    });
  }

  activateSidebarItem(index: number): void {
    const item = this.sidebarItems[index];
    if (item.href === 'add') {
      this.openWebElementsDialogBox();
      return;
    }
    if (item.disabled) return;

    if (index !== this.sidebarExtendedItem) {
      this.sidebarExtendedItem = index;
    } else {
      this.sidebarExtendedItem = -1; // Toggle the same item
    }

    this.shellService.activateNavItem(index, this.sidebarItems);
  }

  activateSidebarSubItem(index: number, subItem: NavMenuItem): void {
    this.shellService.activateNavSubItem(index, subItem, this.sidebarItems);
  }

  openWebElementsDialogBox() {
    this._dialogService.open(AddElementComponent, {
      size: 'lg',
      closeButton: false,
      windowClass: 'dialog-overflow-unset',
      width: '620px',
      height: '832px',
    });
  }
}
